// Navigation.tsx — top nav bar: tab switcher, bot-status badge, and
// runtime Bootswatch theme picker (swaps the #theme-link href).
//
// Links go through react-router's NavLink: the URL is the source of
// truth, and `active` state is derived from the current pathname. App.tsx
// owns the BrowserRouter; this component just reads / writes the URL.
import React, { useEffect, useState } from 'react';
import { Button, Container, Form, Nav, Navbar, Stack } from 'react-bootstrap';
import { BsBoxArrowRight, BsMoonFill, BsSunFill } from 'react-icons/bs';
import { NavLink, useNavigate } from 'react-router-dom';

import { getTheme, schemeOf, setTheme as setCookieTheme } from '../common/theme';

import ThirdEyeLogo from './ThirdEyeLogo';

interface NavigationProps {
  userEmail?: string;
  userPhotoUrl?: string;
  onLogout?: () => void;
}

interface NavItem {
  label: string;
  to: string;
}

// react-bootstrap types Nav.Link's `className` as a plain string, but with
// `as={NavLink}` it is react-router that consumes the prop, and react-router
// also accepts the ({ isActive }) => string render-prop form. The cast states
// that; the value handed to react-router at runtime is unchanged.
const navLinkClassName = (({ isActive }: { isActive: boolean }) =>
  `nav-link${isActive ? ' active' : ''}`) as unknown as string;

const NAV_ITEMS: NavItem[] = [
  { label: 'Metrics', to: '/' },
  { label: 'Sessions', to: '/sessions' },
  { label: 'MCP Servers', to: '/mcp' },
  { label: 'Dreams', to: '/dreams' },
];

const Navigation: React.FC<NavigationProps> = ({
  userEmail,
  userPhotoUrl,
  onLogout,
}) => {
  const [theme, setTheme] = useState(getTheme());
  const navigate = useNavigate();

  useEffect(() => {
    const link = document.getElementById('theme-link') as HTMLLinkElement;
    if (link) {
      link.href = `https://cdn.jsdelivr.net/npm/bootswatch@5.3.3/dist/${theme}/bootstrap.min.css`;
    }
    // In lockstep with the stylesheet (DESIGN.md C-BOOT-2): Bootswatch
    // keys its surface variables off this attribute, so without it the
    // light theme kept the dark surfaces index.html starts with.
    document.documentElement.dataset.bsTheme = schemeOf(theme);
  }, [theme]);

  const toggleTheme = () => {
    const newTheme = theme === 'flatly' ? 'darkly' : 'flatly';
    setTheme(newTheme);
    setCookieTheme(newTheme);
  };

  return (
    <Navbar style={{ backgroundColor: '#303633' }} variant="dark" expand="lg" sticky="top">
      <Container fluid>
        <Navbar.Brand
          href="/"
          onClick={(e) => {
            e.preventDefault();
            navigate('/');
          }}
        >
          <ThirdEyeLogo size={40} color="#a5be00" />
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="basic-navbar-nav" />
        <Navbar.Collapse id="basic-navbar-nav">
          <Nav className="me-auto">
            {NAV_ITEMS.map((item) => (
              <Nav.Link
                key={item.to}
                as={NavLink}
                to={item.to}
                // `end` on the index route ("/") so it isn't flagged active
                // for every nested path.
                end={item.to === '/'}
                className={navLinkClassName}
              >
                {item.label}
              </Nav.Link>
            ))}
          </Nav>
          <Nav className="ms-auto align-items-center">
            <Stack
              direction="horizontal"
              className="align-items-center me-3 border-end pe-3"
              style={{ borderColor: 'rgba(255,255,255,0.2)' }}
            >
              <BsSunFill size={20} className="text-warning" />
              <Form.Check
                type="switch"
                id="theme-switch"
                label=""
                checked={theme === 'darkly'}
                onChange={toggleTheme}
                className="mx-2"
              />
              <BsMoonFill size={20} className="text-light" />
            </Stack>
            {userEmail && (
              <div
                className="d-flex align-items-center gap-2 ms-3 ps-3 border-start"
                style={{ borderColor: 'rgba(255,255,255,0.2)' }}
              >
                {userPhotoUrl ? (
                  <img
                    src={userPhotoUrl}
                    alt={userEmail}
                    title={userEmail}
                    referrerPolicy="no-referrer"
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '1px solid rgba(255,255,255,0.2)',
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <span
                    title={userEmail}
                    aria-label={userEmail}
                    className="d-inline-flex align-items-center justify-content-center text-light"
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      flexShrink: 0,
                    }}
                  >
                    {userEmail.charAt(0)}
                  </span>
                )}
                {onLogout && (
                  <Button
                    variant="outline-light"
                    size="sm"
                    onClick={onLogout}
                    className="d-flex align-items-center gap-1"
                  >
                    <BsBoxArrowRight size={16} />
                  </Button>
                )}
              </div>
            )}
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default Navigation;
